import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { GET_PARK_ZONES } from '@queries/getParkZones';
import { useCallback, useRef, useState } from 'react';
import { featureCountryCode } from 'states/persistInStorage';
import { Query } from 'types/generatedSchemaTypes';
import { updateParkZonesPaginationQuery } from 'utils/parkUtils';

const GET_PARK_ZONES_SIZE = 500;

export const useFetchParkZones = () => {
  const [mergedParkZonesData, setMergedParkZonesData] = useState<Query>();
  const [loading, setLoading] = useState(false);
  const currentParkZonePage = useRef(0);
  const appCountry = useReactiveVar(featureCountryCode);

  const [getParkZones] = useLazyQuery(GET_PARK_ZONES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const fetchMoreParkZones = useCallback(
    async (data: Query): Promise<Query[]> => {
      const promises = [];
      if (data?.parkZonesV2?.totalPages && currentParkZonePage.current === 0) {
        for (let i = 1; i < data?.parkZonesV2?.totalPages; i++) {
          currentParkZonePage.current++;
          promises.push(
            getParkZones({
              variables: {
                input: {
                  size: GET_PARK_ZONES_SIZE,
                  page: currentParkZonePage.current,
                  countryCode: appCountry,
                },
              },
            }).then((res: any) => {
              return res?.data;
            })
          );
        }
        return await Promise.all(promises);
      }
      return [];
    },
    [getParkZones, appCountry]
  );

  const fetchAllParkZones = useCallback(async () => {
    setLoading(true);
    currentParkZonePage.current = 0;

    try {
      // Fetch the first page
      const firstFetch = await getParkZones({
        variables: {
          input: {
            size: GET_PARK_ZONES_SIZE,
            countryCode: appCountry,
            page: 0,
          },
        },
      });

      let mergedData = firstFetch?.data ?? {};
      setMergedParkZonesData(mergedData);

      const paginatedFetches = await fetchMoreParkZones(firstFetch?.data);

      if (paginatedFetches.length > 0) {
        paginatedFetches.forEach((paginatedFetch, i) => {
          mergedData = updateParkZonesPaginationQuery(
            mergedData,
            paginatedFetch
          );
        });
        setMergedParkZonesData(mergedData);
      }
    } finally {
      setLoading(false);
    }
  }, [getParkZones, fetchMoreParkZones, appCountry]);

  return { mergedParkZonesData, fetchAllParkZones, loading };
};
